import { encode, useIntl } from '@capsicum/common';
import styled from 'styled-components';

import { ListCard, SkeletonItem, Text, Tooltip } from '../../atoms';

export type NetworkFeeProps = {
	feeAmount?: null | number;
	feePrice?: null | string;
	testID: string;
	variant: 'free' | 'normal';
};

export const NetworkFeeCard = ({ feeAmount, feePrice, testID, variant = 'normal' }: NetworkFeeProps) => {
	const { $t } = useIntl();
	const hasFreeLabel = variant === 'free';

	return (
		<div>
			<S.FeeCard $hasFreeLabel={hasFreeLabel} testID={testID}>
				<S.FeeCardContent>
					<ListCard.Row>
						<S.FeeTitleWithTooltip>
							<Text variant="text3">{$t('survey.approve.networkFee')}</Text>
							<Tooltip>
								<Tooltip.Trigger testID="ncw-send-tokenAmount-tooltip"></Tooltip.Trigger>
								<Tooltip.Content>
									<Tooltip.Description>
										<Text variant="text3">{$t('survey.approve.networkFeeTooltip')}</Text>
									</Tooltip.Description>
								</Tooltip.Content>
							</Tooltip>
						</S.FeeTitleWithTooltip>
						{feeAmount ? (
							<S.FeeAmount $hasFreeLabel={hasFreeLabel}>{`${feeAmount} CHZ`}</S.FeeAmount>
						) : (
							<SkeletonItem height={20} width={120} />
						)}
					</ListCard.Row>
					<S.ConversionPrice>
						{feePrice ? (
							<>
								<S.FeePrice $hasFreeLabel={hasFreeLabel} variant="text2">
									{encode('doubleTilde')}
								</S.FeePrice>
								<S.FeePrice $hasFreeLabel={hasFreeLabel} variant="text2">
									{feePrice}
								</S.FeePrice>
							</>
						) : (
							<SkeletonItem height={20} width={120} />
						)}
					</S.ConversionPrice>
				</S.FeeCardContent>
			</S.FeeCard>
			{hasFreeLabel && (
				<S.FeeCardLabel>
					<Text variant="caption1">{$t('survey.approve.feeCovered')}</Text>
					<S.FreeLabel>
						<S.FreeLabelText variant="caption1">{$t('survey.approve.free')}</S.FreeLabelText>
					</S.FreeLabel>
				</S.FeeCardLabel>
			)}
		</div>
	);
};

namespace S {
	export const FeeCard = styled(ListCard)<{ $hasFreeLabel: boolean }>`
		padding-inline: ${({ theme }) => theme.spacings.small};
		${({ $hasFreeLabel, theme }) => ($hasFreeLabel ? theme.border.radius.mediumTop : null)};
	`;
	export const FeeCardContent = styled(ListCard.Content)`
		gap: 0;
	`;
	export const FeeTitleWithTooltip = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const FeePrice = styled(Text)<{ $hasFreeLabel: boolean }>`
		color: ${({ $hasFreeLabel, theme }) =>
			$hasFreeLabel ? theme.colors.text.main.disabled : theme.colors.text.main.enabled};
		text-decoration-line: ${({ $hasFreeLabel }) => ($hasFreeLabel ? 'line-through' : 'none')};
	`;
	export const FeeAmount = styled(ListCard.Text)<{ $hasFreeLabel: boolean }>`
		color: ${({ $hasFreeLabel, theme }) =>
			$hasFreeLabel ? theme.colors.text.main.disabled : theme.colors.text.main.enabled};
		text-decoration-line: ${({ $hasFreeLabel }) => ($hasFreeLabel ? 'line-through' : 'none')};
	`;
	export const ConversionPrice = styled(ListCard.Row)`
		justify-content: flex-end;
	`;
	export const FeeCardLabel = styled.div`
		${({ theme }) => theme.border.radius.mediumBottom};
		background-color: ${({ theme }) => theme.colors.backgrounds.info.subtle};
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		padding: ${({ theme }) => theme.spacings.xxxsmall} ${({ theme }) => theme.spacings.small};
	`;
	export const FreeLabel = styled.div`
		${({ theme }) => theme.border.radius.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.brand.selected};
		border: ${({ theme }) => `0.7px solid ${theme.colors.borders.brand.enabled}`};
		padding: 0px ${({ theme }) => theme.spacings.xxsmall};
		border-radius: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const FreeLabelText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
}
